import { useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { NoWorkersBanner, useNoWorkerReason } from "../components/NoWorkersBanner.tsx";
import type { ActorDTO, IdeaRowDTO } from "@roomate/core";
import { useActors, useCreate, useIdeas, useNodes, useProjects } from "../api.ts";
import { Avatar, Bento, Disclosure, Page, PageHeader, Pill, Tile } from "../calm.tsx";
import { IdeaStatus, useIdeaRow } from "../components/IdeaStatus.tsx";
import { ideaClock } from "../idea-clock.ts";
import { ideaSteps, ideaStage, stuckStep } from "../lib/idea-status.ts";
import { ActorName, Ago, Button, cx, Empty, ErrorNote, formatSpan, inputCls, useNow } from "../ui.tsx";
import { useCurrentProject } from "../project.tsx";
import { Dialog } from "./Project.tsx";

export { useIdeas };

const STEPS = ["Filed", "Spec", "Your approval", "Building", "Shipped"];

const STAGE_TONE: Record<ReturnType<typeof ideaStage>["who"], "signal" | "done" | "live" | "neutral"> = {
  you: "signal",
  done: "done",
  agent: "live",
  agents: "live",
  dropped: "neutral",
};

export function IdeasPage() {
  const { projectId, ready } = useCurrentProject();
  // Wait for the project list so the first fetch is already scoped.
  const ideas = useIdeas(projectId || undefined, ready);
  const actors = useActors().data;
  const rows = ideas.data ?? [];
  return (
    <Page wide>
      <PageHeader title="Ideas" meta={<span>One line in, merged code out.</span>} actions={<IdeaButton large />} />
      {rows.length === 0 && !ideas.isLoading && (
        <div className="mt-8">
          <Empty>No ideas yet. The best demo starts with one sentence.</Empty>
        </div>
      )}
      {rows.length > 0 && (
        <div data-testid="ideas">
          <Bento className="mt-8">
            {rows.map((row) => (
              <IdeaCard key={row.intent.id} row={row} actors={actors} />
            ))}
          </Bento>
        </div>
      )}
    </Page>
  );
}

/** One quiet line: how long the idea took to ship, or how long it has been building. Renders nothing when nothing merged and the intent is done. */
function IdeaClockLine({ row }: { row: IdeaRowDTO }) {
  const now = useNow(30_000);
  const clock = ideaClock(row, now);
  if (clock.kind === "none") return null;
  const filed = new Date(row.intent.createdAt).toLocaleString();
  const cls = "tabular-nums";
  if (clock.kind === "shipped") {
    const merged = row.shippedAt == null ? "" : new Date(row.shippedAt).toLocaleString();
    return (
      <>
        <span aria-hidden> · </span>
        <span data-testid="idea-clock" title={`Filed ${filed} → merged ${merged}`} className={cx(cls, "text-[color:var(--ok)]")}>
          Shipped in {formatSpan(clock.ms)}
        </span>
      </>
    );
  }
  return (
    <>
      <span aria-hidden> · </span>
      <span data-testid="idea-clock" title={`Filed ${filed} → ${clock.merging ? "all tasks done, merge pending" : "still building"}`} className={cx(cls, "text-ink-3")}>
        {clock.merging ? `Merging · ${formatSpan(clock.ms)} so far` : `${formatSpan(clock.ms)} so far`}
      </span>
    </>
  );
}

function IdeaCard({ row, actors }: { row: IdeaRowDTO; actors: ActorDTO[] | undefined }) {
  const stage = ideaStage(row);
  const creator = actors?.find((a) => a.id === row.intent.createdBy);
  const done = row.tasks.filter((t) => t.stage === "done" || t.stage === "cancelled").length;
  const nodes = useNodes().data ?? [];
  const stuck = stuckStep(ideaSteps(row, { actors: actors ?? [], nodes }));
  return (
    <Tile span={3} testId="idea-card" className={stage.who === "you" ? "!bg-signal-soft/70" : undefined}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {creator && <Avatar name={creator.name} kind={creator.kind} size={36} />}
          <div className="min-w-0 flex-1">
            <Link to={`/intents/${row.intent.id}`} className="block truncate text-[17px] font-medium tracking-[-0.01em] hover:underline">
              “{row.intent.problem || row.intent.title}”
            </Link>
            <p className="mt-0.5 truncate text-[12.5px] text-ink-3">
              <ActorName id={row.intent.createdBy} actors={actors} /> · <Ago ts={row.intent.createdAt} />
              <IdeaClockLine row={row} />
            </p>
          </div>
        </div>
        <Pill tone={STAGE_TONE[stage.who]} testId="idea-stage" className="shrink-0 whitespace-nowrap">
          {stage.label}
        </Pill>
      </div>

      <ol className="mt-4 flex gap-1" aria-label={`progress: ${stage.label}`}>
        {STEPS.map((s, i) => (
          <li key={s} title={s} className={cx("h-1.5 flex-1 rounded-full", i < stage.step ? "bg-ink" : i === stage.step ? (stage.who === "you" ? "bg-signal" : "bg-ink-2") : "bg-ink/[0.08]")}>
            <span className="sr-only">{s}</span>
          </li>
        ))}
      </ol>

      {stuck?.stuck && (
        <p className="mt-3 text-[13px] text-ink-2" data-testid="idea-card-stuck">
          Stuck: {stuck.stuck.reason}{" "}
          <Link to={stuck.stuck.action.to} className="font-medium text-ink underline">
            {stuck.stuck.action.label}
          </Link>
        </p>
      )}
      <p className="mt-3 text-[13px]">
        <Link to={`/ideas/${row.intent.id}`} className="text-ink-2 hover:text-ink" data-testid="idea-card-status-link">
          What happens next →
        </Link>
      </p>

      {(row.specTask || row.tasks.length > 0) && (
        <Disclosure summary={row.tasks.length > 0 ? `${done}/${row.tasks.length} tasks` : `spec task #${row.specTask!.number}`} className="mt-3">
          <ul className="space-y-1">
            {row.specTask && (
              <li>
                <Link to={`/tasks/${row.specTask.id}`} className="text-[13px] text-ink-2 hover:text-ink">
                  spec task #{row.specTask.number}
                </Link>
              </li>
            )}
            {row.tasks.map((t) => (
              <li key={t.id}>
                <Link to={`/tasks/${t.id}`} className="flex items-center gap-2 rounded-xl px-2 py-1.5 text-[13px] hover:bg-ink/[0.04]">
                  <span className="shrink-0 text-ink-3">#{t.number}</span>
                  <span className="min-w-0 flex-1 truncate">{t.title}</span>
                  <span className={cx("shrink-0 text-[11.5px]", t.stage === "done" ? "text-[color:var(--ok)]" : "text-ink-3")}>{t.stage.replace("_", " ")}</span>
                </Link>
              </li>
            ))}
          </ul>
        </Disclosure>
      )}
    </Tile>
  );
}

/** "+ Idea" — available from every screen, so a founder can drop a thought the moment they have it. */
export function IdeaButton({ large = false }: { large?: boolean }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const projects = useProjects().data ?? [];
  const waiting = useNoWorkerReason() != null;
  // The select defaults to the current project; a pick made in the dialog wins until it is reopened.
  const { projectId: currentId } = useCurrentProject();
  const [picked, setPicked] = useState("");
  const projectId = projects.some((p) => p.id === picked) ? picked : currentId;
  const [filed, setFiled] = useState<{ agent: string | null; intentId: string } | null>(null);
  const create = useCreate<{ specAgent: { name: string } | null; intent: { id: string } }>("/ideas", [["intent"], ["project"], ["overview"]]);
  const filedRow = useIdeaRow(filed?.intentId).row;
  const box = useRef<HTMLTextAreaElement>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      { projectId, text },
      {
        onSuccess: (r) => {
          setFiled({ agent: r.specAgent?.name ?? null, intentId: r.intent.id });
          setText("");
        },
      },
    );
  };

  return (
    <>
      <Button onClick={() => (setOpen(true), setFiled(null), setPicked(""))} variant={large ? "primary" : "ghost"} className={cx(large ? "" : "px-2 py-1 text-[13px] text-ink-2 hover:text-ink")} data-testid="idea-button" title="New idea (⌘I)">
        ＋ Idea
      </Button>
      {open && (
        <Dialog title={filed ? "On it" : "What should we build?"} onClose={() => setOpen(false)}>
          {filed ? (
            <div className="space-y-3 text-[14px]" data-testid="idea-filed">
              <p>{waiting ? "Saved. Nothing starts until an agent can work; here is what happens next." : "Filed. Here is what happens next; it updates as the work moves."}</p>
              <NoWorkersBanner />
              {filedRow && <IdeaStatus row={filedRow} compact />}
              <div className="flex flex-wrap gap-2">
                <Link to={`/ideas/${filed.intentId}`} onClick={() => setOpen(false)} data-testid="idea-follow">
                  <Button variant="primary">Follow this idea</Button>
                </Link>
                <Link to="/ideas" onClick={() => setOpen(false)}>
                  <Button>Follow it on Ideas</Button>
                </Link>
                <Button variant="ghost" onClick={() => setFiled(null)}>
                  Another idea
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <textarea
                ref={box}
                autoFocus
                rows={3}
                className={cx(inputCls, "text-[15px]")}
                placeholder="One sentence is enough. e.g. “Let customers pay monthly.”"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(e);
                }}
                data-testid="idea-text"
                aria-label="Idea"
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <select className={cx(inputCls, "w-auto")} value={projectId} onChange={(e) => setPicked(e.target.value)} aria-label="Project">
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <Button variant="primary" type="submit" disabled={text.trim().length < 3 || !projectId || create.isPending} data-testid="idea-submit">
                  Send to spec-agent ⌘↵
                </Button>
              </div>
              <ErrorNote error={create.error} />
            </form>
          )}
        </Dialog>
      )}
    </>
  );
}
